import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { getTreasuryStats } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Landmark } from "lucide-react";
import { AXIS_TICK, TOOLTIP_STYLE, BAR_CURSOR, BAR_TOP_RADIUS } from "@/lib/chartStyles";

const RANGES = ["24h", "7d", "30d"];
const usd = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 }));
const label = (t, range) =>
  new Date(t * 1000).toLocaleString(undefined, range === "24h" ? { hour: "2-digit" } : { month: "short", day: "numeric" });

const Stat = ({ title, value, testId }) => (
  <div className="p-3 rounded-lg bg-[#050505] border border-[#2A2411]">
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{title}</p>
    <p data-testid={testId} className="font-display text-lg font-bold text-white">{value}</p>
  </div>
);

const RecentInflows = ({ rows }) => (
  <div className="divide-y divide-[#2A2411]">
    {rows.length === 0 && <p data-testid="treasury-empty" className="text-sm text-zinc-500 py-3">No buy or sell trades yet</p>}
    {rows.map((r) => (
      <div key={r.txHash} data-testid="treasury-inflow-row" className="flex items-center justify-between py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.kind === "buy" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>{r.kind}</span>
          <Link to={`/tx/${r.txHash}`} className="table-link font-mono text-xs truncate">{r.txHash.slice(0, 14)}…</Link>
        </div>
        <span className="font-mono text-xs text-[#D4AF37]">+{usd(r.fee + r.tax)} USDT</span>
      </div>
    ))}
  </div>
);

export const TreasuryDashboard = () => {
  const [range, setRange] = useState("7d");
  const { data, isError } = useQuery({ queryKey: ["treasury", range], queryFn: () => getTreasuryStats(range), refetchInterval: 10000 });

  return (
    <Card data-testid="status-treasury" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2"><Landmark size={16} className="text-[#D4AF37]" /> FSC Treasury</CardTitle>
        <div className="flex gap-1 rounded-full border border-[#2A2411] p-0.5">
          {RANGES.map((r) => (
            <button key={r} data-testid={`treasury-range-${r}`} onClick={() => setRange(r)}
              className={`px-2.5 py-1 text-xs rounded-full transition-colors ${range === r ? "bg-[#D4AF37] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError && <p data-testid="treasury-error" className="text-sm text-zinc-500">Treasury data unavailable</p>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat title="Treasury balance" value={`${usd(data.usdtBalance)} USDT`} testId="treasury-balance" />
              <Stat title="Fees (all time)" value={`${usd(data.allTime.fees)} USDT`} testId="treasury-fees-total" />
              <Stat title="Tax (all time)" value={`${usd(data.allTime.tax)} USDT`} testId="treasury-tax-total" />
              <Stat title={`Trades (${range})`} value={`${data.period.trades} · ${usd(data.period.fees + data.period.tax)} USDT`} testId="treasury-period" />
            </div>
            <div data-testid="treasury-chart" className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.buckets}>
                  <XAxis dataKey="t" tickFormatter={(t) => label(t, range)} tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={16} />
                  <Tooltip cursor={BAR_CURSOR} labelFormatter={(t) => label(t, range)}
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v, name) => [`${usd(v)} USDT`, name === "fees" ? "Fees" : "Tax"]} />
                  <Bar dataKey="fees" stackId="a" fill="#D4AF37" />
                  <Bar dataKey="tax" stackId="a" fill="#8a6f1f" radius={BAR_TOP_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-zinc-600 font-mono break-all">Treasury: <Link to={`/address/${data.treasury}`} className="table-link">{data.treasury}</Link></p>
            <RecentInflows rows={data.recent} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
